// SocketContext.tsx
import React, { createContext, useContext, useRef, useEffect, useState } from 'react';
import { Outlet } from 'react-router';

const socketURL = `${process.env.REACT_APP_WEBSOCKET_URL}`;

interface SocketContextType {
    socketRef: React.RefObject<WebSocket | undefined>;
    initFunction: React.RefObject<((ws: WebSocket) => void) | undefined>;
    onMessage: React.RefObject<((msg: MessageEvent) => void) | undefined>;
    isOpen: boolean;
}

const SocketContext = createContext<SocketContextType | null>(null);

export function SocketProvider() {
    const socketRef = useRef<WebSocket | undefined>(undefined);
    const initFunction = useRef<((ws: WebSocket) => void) | undefined>(undefined);
    const onMessage = useRef<((msg: MessageEvent) => void) | undefined>(undefined);
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        if (socketRef.current) {
            console.log("Socket already created");
            return;
        }

        const socket = new WebSocket(socketURL);
        socketRef.current = socket;

        socket.onopen = () => setIsOpen(true);

        socket.onclose = () => {
            setIsOpen(false);
            socketRef.current = undefined;
            initFunction.current = undefined;
            console.log('WebSocket disconnected');
        };

        socket.onerror = (err) => console.error('WebSocket error:', err);

        if (initFunction.current) {
            initFunction.current(socket);
        }

        if (onMessage.current) {
            socket.onmessage = onMessage.current;
            onMessage.current = undefined;
        }

        return () => {
            socket.close();
            socketRef.current = undefined;
        };
    }, []);

    return (
        <SocketContext.Provider value={{ socketRef, initFunction, onMessage, isOpen }}>
            <Outlet />
        </SocketContext.Provider>
    );
}

export function useSocket(
    onmessage?: (this: WebSocket, msg: MessageEvent) => void,
    onOpen?: (ws: WebSocket) => void
): WebSocket | undefined {

    const context = useContext(SocketContext);
    if (!context) throw new Error("useSocket must be used within SocketProvider");
    const { socketRef, initFunction, onMessage, isOpen } = context;

    useEffect(() => {
        if (socketRef.current && onmessage) {
            socketRef.current.onmessage = onmessage;
        } else {
            onMessage.current = onmessage;
        }
    }, [onmessage]);

    useEffect(() => {
        const ws = socketRef.current;
        if (!ws || !onOpen) return;

        if (isOpen) {
            onOpen(ws);
        }
    }, [isOpen, onOpen]);

    return socketRef.current;
}